import { normalizeId } from './ids';
import getItemDisplayValue from './items';

export const LOOKUP_DATA_TYPE = 'card-lookup';

const EMPTY = [];

/** The ids a card-lookup element value points at, single or `allowMultiple`. */
export const extractLookupIds = (rawValue) => {
	const values = Array.isArray(rawValue) ? rawValue : [rawValue];
	return values.map(val => normalizeId(val)).filter(Boolean);
};

/**
 * Every item id referenced by an item's card-lookup elements.
 * `getElementsForCard` must include inherited elements (the card-definitions
 * context resolver already does). `entries` also reads a repeating group's lookup
 * columns.
 */
export const collectLookupIds = (item, getElementsForCard, { entries = false } = {}) => {
	if (!item || typeof getElementsForCard !== 'function') return EMPTY;
	const defs = getElementsForCard(item?.card_id) || EMPTY;
	const ids = [];
	defs.forEach(def => {
		// The lookup columns of a repeating group, for a surface that prints its entries
		// one by one and has no other way to a title (a form's ruled lines, KPR-1863).
		if (entries && def?.dataType === 'repeating-group') {
			const columns = (def.options?.columns || EMPTY).filter(column => column?.name && column.dataType === LOOKUP_DATA_TYPE);
			const rows = Array.isArray(item?.elements?.[def.name]) ? item.elements[def.name] : EMPTY;
			if (columns.length) rows.forEach(row => columns.forEach(column => extractLookupIds(row?.elements?.[column.name]).forEach(id => ids.push(id))));
			return;
		}
		if (def?.dataType !== LOOKUP_DATA_TYPE) return;
		extractLookupIds(item?.elements?.[def?.name]).forEach(id => ids.push(id));
	});
	return ids;
};

/**
 * Resolve a card-lookup element value to the referenced item's display label,
 * so a display template that nests a lookup renders human-readable text
 * instead of a raw id.
 *
 * Every label surface shares this one implementation: item cards, the related
 * items list, the relate-item picker, the item form title, and the lookup
 * input's own options. Each differs only in the pool it resolves against.
 *
 * Behavior worth knowing:
 * - Resolution recurses, so a lookup whose own title templates another lookup
 *   resolves all the way down.
 * - A cycle (A titles B, B titles A) falls back to the raw id at the point it
 *   would repeat rather than recursing forever.
 * - `allowMultiple` values render comma-joined.
 * - An id missing from the pool falls back to the raw id, and is NOT cached —
 *   the item may still arrive (see useLinkedItems), which rebuilds the
 *   resolver with a fuller pool.
 * - `suppressIds` resolves to an empty string, for the item form's own title:
 *   an item that references itself should not recurse into its own template.
 *
 * @param {object|Map} itemsById pool of known items, keyed by normalized id
 * @param {object} cardsById card definitions by normalized id
 * @param {Function} getElementsForCard card id -> element defs (with inherited)
 * @param {Set<string>} [suppressIds] ids that resolve to '' instead of a label
 */
export function createLookupResolver({
	itemsById,
	cardsById,
	getElementsForCard,
	suppressIds,
} = {}) {
	const labelCache = new Map();
	const resolving = new Set();
	const resolveElements = typeof getElementsForCard === 'function'
		? cardId => getElementsForCard(cardId) || EMPTY
		: () => EMPTY;
	const lookupItem = itemsById instanceof Map
		? id => itemsById.get(id)
		: id => itemsById?.[id];

	const resolveOne = (rawValue) => {
		const id = normalizeId(rawValue) || (typeof rawValue === 'string' ? rawValue : '');
		if (!id) return '';
		if (suppressIds?.has(id)) return '';
		if (labelCache.has(id)) return labelCache.get(id);
		if (resolving.has(id)) return id;

		const target = lookupItem(id);
		if (!target) return id;

		resolving.add(id);
		try {
			const label = getItemDisplayValue({
				card: cardsById?.[normalizeId(target?.card_id)],
				elementDefs: resolveElements(target?.card_id),
				item: target,
				resolveLookupValue: resolver,
			}) || id;
			labelCache.set(id, label);
			return label;
		} finally {
			resolving.delete(id);
		}
	};

	const resolver = (rawValue) => {
		if (Array.isArray(rawValue)) {
			return rawValue.map(resolveOne).filter(Boolean).join(', ');
		}
		return resolveOne(rawValue);
	};

	// The referenced record itself, and the card it wears, for a renderer
	// that shows more than the label — the qualified badge of a reference
	// into another collection (components/common/LookupRefs). Only a POOL
	// resolver has them: the public page's title map
	// (createTitleMapResolver) discloses titles and nothing else.
	resolver.targetOf = (rawValue) => {
		const id = normalizeId(rawValue);
		return id ? lookupItem(id) || null : null;
	};
	resolver.cardOf = (rawValue) => {
		const target = resolver.targetOf(rawValue);
		return target ? cardsById?.[normalizeId(target.card_id)] || null : null;
	};

	return resolver;
}

/**
 * Resolve card-lookup values against a plain { itemId: title } map instead of
 * an item pool — for the public shared page, where the server discloses
 * pre-rendered titles for exactly the references the link may reveal and
 * nothing else. An id missing from the map resolves to '' (the field renders
 * the same em-dash an empty value gets), never the raw id: the map IS the
 * disclosure boundary, so a miss means "not yours to see", not "not loaded
 * yet".
 */
export function createTitleMapResolver(titlesById) {
	const titles = (titlesById && typeof titlesById === 'object') ? titlesById : {};
	return (rawValue) => extractLookupIds(rawValue)
		.map(id => (typeof titles[id] === 'string' ? titles[id].trim() : ''))
		.filter(Boolean)
		.join(', ');
}

/**
 * THE omit rule, in one place: what a card-lookup value READS as.
 *
 * `createLookupResolver` hands back the raw id when the pool has not
 * delivered the referenced item — still loading, deleted, or not readable by
 * this viewer — and an id is not a reading of anything. This turns that miss
 * into nothing, which is what every read surface prints (the em-dash an empty
 * value gets, or a `[Trailer]` placeholder inside a title) and what the API's
 * own exporter does with the same value (utils/export/valueFormat lookupText).
 *
 * Idempotent over a resolver that already omits — the public page's title map
 * (createTitleMapResolver) never returns an id — so a caller can safely wrap
 * whatever resolver it was handed. No resolver at all means no pool, which is
 * a miss for every id.
 */
export function resolveLookupText(resolver, rawValue) {
	if (typeof resolver !== 'function') return '';
	return extractLookupIds(rawValue)
		.map(id => {
			const text = resolver(id);
			return text === id ? '' : text;
		})
		.filter(Boolean)
		.join(', ');
}

/** `resolveLookupText` as a resolver, for the callers that pass one along (it keeps the pool's `targetOf`/`cardOf`). */
export const omitUnresolvedLookups = resolver => {
	const wrapped = rawValue => resolveLookupText(resolver, rawValue);
	if (typeof resolver?.targetOf === 'function') wrapped.targetOf = resolver.targetOf;
	if (typeof resolver?.cardOf === 'function') wrapped.cardOf = resolver.cardOf;
	return wrapped;
};

export default createLookupResolver;
